import React, { Suspense, lazy, Component, ErrorInfo, ReactNode } from 'react';

// Loading fallback component
const LoadingFallback: React.FC<{ message?: string }> = ({ message = "Loading..." }) => (
  <div className="flex items-center justify-center min-h-[400px]">
    <div className="flex flex-col items-center gap-4">
      <div className="relative">
        <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
        <div className="absolute inset-0 w-12 h-12 border-4 border-transparent border-r-purple-400 rounded-full animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}></div>
      </div>
      <div className="text-center">
        <p className="text-lg font-medium text-gray-700">{message}</p>
        <p className="text-sm text-gray-500 mt-1">Please wait while we load this content...</p>
      </div>
    </div>
  </div>
);

// Custom Error Boundary Component
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center max-w-md mx-auto p-6">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Something went wrong</h3>
            <p className="text-gray-600 mb-4">
              We encountered an error while loading this content. Please try again.
            </p>
            <button
              onClick={() => this.setState({ hasError: false, error: null })}
              className="btn btn-primary"
            >
              Try Again
            </button>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mt-4 text-left">
                <summary className="cursor-pointer text-sm text-gray-500">Error Details</summary>
                <pre className="mt-2 text-xs text-red-600 bg-red-50 p-2 rounded overflow-auto">
                  {this.state.error.message}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Lazy route wrapper component
interface LazyRouteProps {
  children: React.ReactNode;
  loadingMessage?: string;
  fallback?: React.ComponentType;
}

const LazyRoute: React.FC<LazyRouteProps> = ({ 
  children, 
  loadingMessage,
  fallback: CustomFallback 
}) => {
  const FallbackComponent = CustomFallback || (() => <LoadingFallback message={loadingMessage} />);

  return (
    <ErrorBoundary
      onError={(error, errorInfo) => {
        console.error('Lazy route error:', error, errorInfo);
        // Track error in analytics
        if (typeof window !== 'undefined' && 'gtag' in window) {
          (window as any).gtag('event', 'exception', {
            description: error.message,
            fatal: false
          });
        }
      }}
    >
      <Suspense fallback={<FallbackComponent />}>
        {children}
      </Suspense>
    </ErrorBoundary>
  );
};

// Higher-order component for lazy loading routes
export const withLazyRoute = <P extends object>(
  Component: React.ComponentType<P>,
  loadingMessage?: string
) => {
  return (props: P) => (
    <LazyRoute loadingMessage={loadingMessage}>
      <Component {...props} />
    </LazyRoute>
  );
};

// Lazy load a component with retry mechanism
export const createLazyComponent = <P extends object>(
  importFunction: () => Promise<{ default: React.ComponentType<P> }>,
  loadingMessage?: string,
  maxRetries: number = 3
) => {
  const LazyComponent = lazy(() => {
    const retry = (attempt: number): Promise<{ default: React.ComponentType<P> }> => {
      return importFunction().catch(error => {
        if (attempt < maxRetries) {
          console.warn(`Lazy loading attempt ${attempt + 1} failed, retrying...`, error);
          return new Promise(resolve => {
            setTimeout(() => resolve(retry(attempt + 1)), 1000 * Math.pow(2, attempt));
          });
        }
        throw error;
      });
    };
    return retry(0);
  });

  return (props: P) => (
    <LazyRoute loadingMessage={loadingMessage}>
      <LazyComponent {...props} />
    </LazyRoute>
  );
};

export default LazyRoute;
